import { Pause, Play, Trash2, Calendar } from "lucide-react";
import type { IRecurringTaskDefinition, RecurrenceRule } from "../../types";

function describeRule(rule: RecurrenceRule): string {
  switch (rule.type) {
    case "daily":
      return rule.interval === 1 ? "Every day" : `Every ${rule.interval} days`;
    case "weekly": {
      const days = rule.days_of_week.join(", ");
      return rule.interval === 1
        ? `Weekly on ${days}`
        : `Every ${rule.interval} weeks on ${days}`;
    }
    case "monthly": {
      const day =
        rule.day_of_month === "last" ? "last day" : `day ${rule.day_of_month}`;
      const adj =
        rule.business_day_adjustment === "none"
          ? ""
          : rule.business_day_adjustment === "prev"
            ? " (prev business day)"
            : " (next business day)";
      return rule.interval === 1
        ? `Monthly on ${day}${adj}`
        : `Every ${rule.interval} months on ${day}${adj}`;
    }
  }
}

interface RecurringTaskItemProps {
  definition: IRecurringTaskDefinition;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function RecurringTaskItem({
  definition: def,
  onPause,
  onResume,
  onDelete,
}: RecurringTaskItemProps) {
  return (
    <div
      className={`bg-surface border rounded-lg p-4 ${
        def.is_active ? "border-border" : "border-border opacity-60"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-foreground truncate">
            {def.title}
          </h3>
          {def.description && (
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">
              {def.description}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {describeRule(def.recurrence_rule)}
            </span>
            {def.next_due_date && (
              <span className="text-xs text-muted-foreground">
                Next: {def.next_due_date}
              </span>
            )}
            {!def.is_active && (
              <span className="text-xs font-medium text-warning px-1.5 py-0.5 rounded bg-warning/10">
                Paused
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {def.is_active ? (
            <button
              onClick={() => onPause(def.id)}
              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Pause"
            >
              <Pause className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => onResume(def.id)}
              className="p-1.5 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
              title="Resume"
            >
              <Play className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onDelete(def.id)}
            className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
